<template>
  <div class="flex flex-col h-full">
    <div
      class="flex justify-between items-center border-b border-gray-200 py-5 px-16"
    >
      <p class="font-bold text-lg">
        <icon name="user" class="mr-2" />{{
          $t('settings.manageAccount.title')
        }}
      </p>
    </div>
    <div class="py-8 px-16">
      <div
        v-if="currentAdmin"
        class="w-full bg-white border shadow-sm rounded-lg mb-32 p-8"
      >
        <div class="mb-14">
          <p class="font-bold mb-5">
            {{ $t('settings.manageAccount.yourAccount') }}
          </p>
          <div class="flex items-center gap-3">
            <div
              class="flex-none w-12 h-12 rounded-full bg-[#2A72A9] flex items-center justify-center"
            >
              <p class="text-white font-medium text-lg">
                {{
                  currentAdmin.firstName.charAt(0).toUpperCase() +
                  currentAdmin.lastName.charAt(0).toUpperCase()
                }}
              </p>
            </div>
            <div>
              <p class="text-md font-bold text-gray-800">
                {{ currentAdmin.firstName }} {{ currentAdmin.lastName }}
                <span v-if="currentAdmin.mainAdmin" class="text-xs">
                  ({{ $t('settings.manageAccount.mainAdmin') }})
                </span>
              </p>
              <p class="text-sm text-gray-500">{{ currentAdmin.email }}</p>
            </div>
            <div
              @click="showAdminEditModal = true"
              class="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg cursor-pointer ml-4"
            >
              <icon name="edit" class="w-4 h-4 fill-gray-500" />
            </div>
          </div>
        </div>
        <p class="font-bold mb-5">
          {{ $t('settings.manageAccount.otherAdmins') }}
        </p>
        <div class="flex flex-col border border-gray-200 rounded-lg">
          <div class="flex items-center border-b border-gray-200 py-2">
            <div class="w-[30%] p-3">
              <p class="font-medium pl-3">
                {{ $t('settings.manageAccount.name') }}
              </p>
            </div>
            <div class="w-[25%] p-3">
              <p class="font-medium">
                {{ $t('settings.manageAccount.email') }}
              </p>
            </div>
            <div class="w-[20%] p-3">
              <p class="font-medium">
                {{ $t('settings.manageAccount.registration') }}
              </p>
            </div>
            <div class="w-[15%] p-3">
              <p class="font-medium">
                {{ $t('settings.manageAccount.status') }}
              </p>
            </div>
            <div v-if="connexionEmail == mainAdmin.email" class="w-[10%] p-3">
              <p class="font-medium">
                {{ $t('settings.manageAccount.actions') }}
              </p>
            </div>
          </div>
          <div
            v-for="(account, index) in otherAdmins"
            :key="account.did"
            class="flex items-center border-b border-gray-200 py-2"
            :class="{
              'border-b-0': index == otherAdmins.length - 1,
            }"
          >
            <div class="w-[30%] flex items-center gap-2 p-3">
              <div
                class="flex-none w-12 h-12 rounded-full bg-[#2A72A9] flex items-center justify-center ml-2"
              >
                <p class="text-white font-medium text-lg">
                  {{
                    account.firstName.charAt(0).toUpperCase() +
                    account.lastName.charAt(0).toUpperCase()
                  }}
                </p>
              </div>
              <p class="pl-2 break-all">
                {{ account.firstName }} {{ account.lastName }}
                <span v-if="account.mainAdmin" class="text-xs">
                  ({{ $t('settings.manageAccount.mainAdmin') }})
                </span>
              </p>
            </div>
            <div class="w-[25%] flex items-center gap-2 p-3">
              <p class="text-sm pl-2 break-all">{{ account.email }}</p>
            </div>
            <div class="w-[20%] p-3">
              <p v-if="account.registrationDate" class="">
                {{
                  new Date(account.registrationDate).toLocaleDateString(
                    'en-US',
                    {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    }
                  )
                }}
              </p>
              <p v-else class="text-gray-500">-</p>
            </div>
            <div class="w-[15%] p-3">
              <p v-if="account.registrationDate" class="pl-2">
                <icon name="check" class="w-7 h-7 fill-blue-400" />
              </p>
              <p v-else class="text-blue-500 pl-2">
                {{ $t('settings.manageAccount.pending') }}
              </p>
            </div>
            <div
              v-if="connexionEmail == mainAdmin.email"
              class="w-[10%] flex items-center gap-4 p-3"
            >
              <div
                class="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg cursor-pointer"
                @click="deleteAdmin(account.email)"
              >
                <icon name="trash" class="w-5 h-5 fill-gray-500" />
              </div>
            </div>
          </div>
          <div v-if="otherAdmins.length == 0" class="flex items-center p-4">
            <p class="text-gray-500 text-sm">
              {{ $t('settings.manageAccount.noOtherAdmins') }}
            </p>
          </div>
        </div>
        <div v-if="currentAdmin" class="mt-8">
          <button
            @click="showAddAdminModal = true"
            class="bg-secondary text-white shadow-sm font-medium rounded-lg px-4 py-2 flex items-center gap-2"
          >
            <icon name="user-add" class="w-4 h-4 fill-white" />
            {{ $t('settings.manageAccount.addAdmin') }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="showAddAdminModal">
    <div class="fixed top-0 left-0 w-full h-full z-10">
      <div class="absolute w-full h-full bg-black bg-opacity-20"></div>
    </div>
    <BasicSmModal
      @close="
        () => {
          showAddAdminModal = false;
          otherAdminEmail = '';
          otherAdminFirstName = '';
          otherAdminLastName = '';
        }
      "
      absoluteClose
    >
      <p class="font-bold mb-7">{{ $t('settings.manageAccount.addAdmin') }}</p>
      <div class="">
        <div class="flex gap-2 mb-3">
          <input
            type="text"
            :placeholder="$t('settings.manageAccount.firstNamePlaceholder')"
            class="w-[50%] h-9 text-sm outline-none rounded-lg border border-gray-200"
            v-model="otherAdminFirstName"
            maxlength="50"
          />
          <input
            type="text"
            :placeholder="$t('settings.manageAccount.lastNamePlaceholder')"
            class="w-[50%] h-9 text-sm outline-none rounded-lg border border-gray-200"
            v-model="otherAdminLastName"
            maxlength="50"
          />
        </div>
        <input
          type="text"
          :placeholder="$t('settings.manageAccount.emailPlaceholder')"
          class="w-[100%] h-9 text-sm outline-none rounded-lg border border-gray-200 mb-3"
          v-model="otherAdminEmail"
          maxlength="50"
        />
        <div class="flex justify-end">
          <button
            @click="addAdmin()"
            class="w-[25%] h-9 bg-secondary text-sm text-white shadow-sm font-medium rounded-lg px-3 cursor-pointer"
            :class="{
              'opacity-50 cursor-not-allowed pointer-events-none':
                otherAdminEmail.length === 0 ||
                otherAdminFirstName.length === 0,
            }"
          >
            {{ $t('settings.manageAccount.add') }}
          </button>
        </div>
      </div>
    </BasicSmModal>
  </div>
  <div v-if="showAdminEditModal">
    <div class="fixed top-0 left-0 w-full h-full z-10">
      <div class="absolute w-full h-full bg-black bg-opacity-20"></div>
    </div>
    <BasicSmModal @close="showAdminEditModal = false" absoluteClose>
      <p class="font-bold mb-7">{{ $t('settings.manageAccount.editAdmin') }}</p>
      <div class="">
        <div class="flex gap-2 mb-3">
          <input
            type="text"
            :placeholder="$t('settings.manageAccount.firstNamePlaceholder')"
            class="w-[50%] h-9 text-sm outline-none rounded-lg border border-gray-200"
            v-model="currentAdminFirstName"
            maxlength="50"
          />
          <input
            type="text"
            :placeholder="$t('settings.manageAccount.lastNamePlaceholder')"
            class="w-[50%] h-9 text-sm outline-none rounded-lg border border-gray-200"
            v-model="currentAdminLastName"
            maxlength="50"
          />
        </div>
        <input
          type="text"
          :placeholder="$t('settings.manageAccount.emailPlaceholder')"
          class="w-[100%] h-9 text-sm outline-none rounded-lg border border-gray-200 mb-3"
          v-model="currentAdminEmail"
          maxlength="50"
        />
        <div class="flex justify-end">
          <button
            @click="updateAdmin()"
            class="w-[25%] h-9 bg-secondary text-sm text-white shadow-sm font-medium rounded-lg px-3 cursor-pointer"
            :class="{
              'opacity-50 cursor-not-allowed pointer-events-none':
                currentAdminFirstName.length === 0 ||
                currentAdminLastName.length === 0 ||
                currentAdminEmail.length === 0,
            }"
          >
            {{ $t('settings.manageAccount.save') }}
          </button>
        </div>
      </div>
    </BasicSmModal>
  </div>
  <div v-if="showConfirmationCodeModal">
    <div class="fixed top-0 left-0 w-full h-full z-10">
      <div class="absolute w-full h-full bg-black bg-opacity-20"></div>
    </div>
    <BasicSmModal @close="showConfirmationCodeModal = false" absoluteClose>
      <p class="font-bold mb-7">
        {{ $t('settings.manageAccount.confirmationCode') }}
      </p>
      <div class="">
        <input
          type="text"
          :placeholder="
            $t('settings.manageAccount.confirmationCodePlaceholder')
          "
          class="w-[100%] h-9 text-sm outline-none rounded-lg border border-gray-200 mb-3"
          v-model="confirmationCode"
          maxlength="6"
        />
        <div class="flex justify-end">
          <button
            @click="confirmEmail()"
            class="w-[25%] h-9 bg-secondary text-sm text-white shadow-sm font-medium rounded-lg px-3 cursor-pointer"
          >
            {{ $t('settings.manageAccount.confirm') }}
          </button>
        </div>
      </div>
    </BasicSmModal>
  </div>
</template>

<script>
import { useStore } from 'vuex';
import { computed, ref, inject, watch } from 'vue';
import BasicSmModal from '../../components/BasicSmModal.vue';
import { useI18n } from 'vue-i18n';

export default {
  setup() {
    const store = useStore();
    const utils = inject('utils');
    const api = inject('api');
    const cookies = inject('cookies');
    const { t } = useI18n();

    const isWeb2Connexion = computed(() => store.getters.isWeb2Connexion);
    const connexionEmail = computed(() => store.getters.getConnexionEmail);

    const mainAdmin = ref(null);
    const currentAdmin = ref(null);
    const currentAdminFirstName = ref('');
    const currentAdminLastName = ref('');
    const currentAdminEmail = ref('');
    const otherAdmins = ref([]);
    const showAddAdminModal = ref(false);
    const otherAdminEmail = ref('');
    const otherAdminFirstName = ref('');
    const otherAdminLastName = ref('');
    const showAdminEditModal = ref(false);
    const showConfirmationCodeModal = ref(false);
    const confirmationCode = ref('');

    async function getAccounts() {
      api.getIssuerAdmins().then((res) => {
        const admins = res.data;
        mainAdmin.value = admins.find((admin) => admin.mainAdmin);
        currentAdmin.value = admins.find(
          (admin) => admin.email === connexionEmail.value
        );
        currentAdminFirstName.value = currentAdmin.value.firstName;
        currentAdminLastName.value = currentAdmin.value.lastName;
        currentAdminEmail.value = currentAdmin.value.email;
        otherAdmins.value = admins
          .filter((admin) => admin.email !== connexionEmail.value)
          .sort((a, b) => {
            if (a.mainAdmin) return -1;
            if (b.mainAdmin) return 1;
            return 0;
          });
      });
    }
    getAccounts();

    async function addAdmin() {
      // check input
      if (otherAdminEmail.value.length === 0) {
        utils.showToastMessageError(t('settings.manageAccount.emailRequired'));
        return;
      }
      if (
        otherAdminFirstName.value.length === 0 ||
        otherAdminLastName.value.length === 0
      ) {
        utils.showToastMessageError(t('settings.manageAccount.nameRequired'));
        return;
      }
      if (
        !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
          otherAdminEmail.value
        )
      ) {
        utils.showToastMessageError(t('settings.manageAccount.invalidEmail'));
        return;
      }

      api
        .addIssuerAdmin({
          email: otherAdminEmail.value,
          firstName: otherAdminFirstName.value,
          lastName: otherAdminLastName.value,
        })
        .then(() => {
          showAddAdminModal.value = false;
          otherAdminEmail.value = '';
          otherAdminFirstName.value = '';
          otherAdminLastName.value = '';
          getAccounts();
        });
    }

    async function deleteAdmin(email) {
      api.deleteIssuerAdmin(email).then(() => {
        getAccounts();
      });
    }

    async function updateAdmin() {
      api
        .updateIssuerAdmin({
          firstName: currentAdminFirstName.value,
          lastName: currentAdminLastName.value,
          oldEmail: currentAdmin.value.email,
          newEmail: currentAdminEmail.value,
        })
        .then((res) => {
          showAdminEditModal.value = false;
          if (res.data.emailSent) {
            showConfirmationCodeModal.value = true;
          }
          getAccounts();
        });
    }

    async function confirmEmail() {
      api
        .confirmIssuerAdminEmail({
          code: confirmationCode.value,
        })
        .then((res) => {
          if (res.data.success) {
            const token = res.data.token;
            cookies.set('token', token);
            window.location.reload();
          }
        })
        .catch((err) => {
          utils.showToastMessageError(err.response.data.message);
        });
    }

    return {
      currentAdmin,
      otherAdmins,
      showAddAdminModal,
      mainAdmin,
      otherAdminEmail,
      otherAdminFirstName,
      otherAdminLastName,
      showAdminEditModal,
      currentAdminFirstName,
      currentAdminLastName,
      currentAdminEmail,
      addAdmin,
      updateAdmin,
      connexionEmail,
      deleteAdmin,
      showConfirmationCodeModal,
      confirmationCode,
      confirmEmail,
    };
  },
  components: {
    BasicSmModal,
  },
};
</script>
